/* ==========================================================================
   ZENVOX — Carrito lateral
   --------------------------------------------------------------------------
   Acompaña a sections/zenvoxcarrito.liquid.

   Cómo se mantiene al día
   -----------------------
   Cada cambio del carrito se manda con el parámetro `sections` de la Cart
   AJAX API. Shopify aplica el cambio Y devuelve el HTML de la sección ya
   renderizado en la MISMA respuesta. Los precios, los descuentos y los
   totales los sigue calculando Liquid.

   La alternativa —leer /cart.js y armar el HTML en JavaScript— obliga a
   reimplementar el formato de moneda y las reglas de descuento, que es
   justamente donde estos carritos se rompen y muestran un total que no es.

   Antes esto eran dos viajes de red encadenados (POST al carrito, y recién
   después un GET ?sections=): medidos contra la tienda daban 0,77 s + 0,42 s
   con el panel quieto en el medio. Ahora es uno solo.

   Cómo se siente rápido
   ---------------------
   Tres reglas, y las tres importan:

   1. El panel se abre ANTES de que salga el pedido. Nunca se espera a la red
      para mover algo en pantalla.
   2. La cantidad se pinta al toque y recién después se confirma contra el
      servidor. Si el servidor dice otra cosa, manda el servidor.
   3. Los pedidos van de a uno, en fila. Dos `change.js` en vuelo al mismo
      tiempo son una lotería: `line` es un índice, y si una línea se borra
      los índices se corren abajo del pedido que ya salió.
   ========================================================================== */

(function () {
  const SECCION = 'zenvoxcarrito';

  // El boton del carrito del encabezado es de Horizon. Su sistema de eventos
  // solo llama a componentes de su propia clase, asi que este carrito escucha
  // el clic por su cuenta.
  const DISPARADOR = '[data-testid="cart-drawer-trigger"], [href$="/cart"]';

  /* ---------------- Pedidos al carrito ---------------- */

  // Cuerpo JSON. Aca `sections_url` se puede mandar, y conviene: es exacto.
  function pedirJSON(ruta, datos, seccion) {
    const cuerpo = Object.assign({}, datos, {
      sections: seccion,
      sections_url: window.location.pathname,
    });

    return fetch(ruta, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(cuerpo),
    }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
  }

  // Cuerpo de formulario, que es lo que mandan los botones "Agregar".
  //
  // OJO: aca `sections_url` NO va. Verificado contra la tienda el 2026-08-12:
  // con el cuerpo en multipart o en urlencoded, Shopify contesta 400
  // {"errors":"sections_url must be a relative path"} para CUALQUIER valor,
  // incluso "/". Solo lo acepta cuando el cuerpo es JSON. Omitiendolo contesta
  // 200 y arma la seccion a partir del Referer, que el navegador manda solo en
  // un fetch al mismo dominio. Si algun dia no viniera, `aplicar` se cae al
  // camino largo y el carrito igual queda bien.
  function pedirForm(ruta, datos, seccion) {
    datos.append('sections', seccion);

    return fetch(ruta, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: datos,
    }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
  }

  // Deja el boton de "Agregar" ocupado mientras dura el pedido, y devuelve la
  // funcion que lo suelta. El :not([disabled]) del selector no es adorno: sin
  // el, un producto sin stock quedaria habilitado al soltarlo.
  function ocupar(form) {
    const boton = form.querySelector('button[type="submit"]:not([disabled])');
    if (!boton) return function () {};

    boton.disabled = true;
    boton.setAttribute('aria-busy', 'true');

    return function () {
      boton.disabled = false;
      boton.removeAttribute('aria-busy');
    };
  }

  class ZenvoxCarrito extends HTMLElement {
    // Pedidos que todavia no volvieron. Solo repinta el ultimo: sin esto, con
    // tres clics seguidos en "+" el numero sube de a uno a destiempo, como si
    // el carrito estuviera contando solo.
    pendientes = 0;

    // Fila de pedidos. Ver la regla 3 del encabezado.
    cola = Promise.resolve();

    connectedCallback() {
      // Delegacion en el host: el morph de Horizon reemplaza los hijos sin
      // desconectar este elemento, y ademas cada refresco vuelve a escribir
      // el contenido. Nada de guardar referencias a hijos.
      this.addEventListener('click', this.alClick);
      this.cerrando = this.cerrando.bind(this);
      document.addEventListener('keydown', this.cerrando);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.cerrando);
      this.soltarPagina();
    }

    /* ---------------- Abrir y cerrar ---------------- */

    // `esperando` es para cuando se abre por un "Agregar" que todavia no
    // volvio. Solo pone el esqueleto si no hay nada que mostrar: con el
    // carrito ya cargado es mejor ver los articulos de antes un instante que
    // ver placeholders grises donde habia contenido real.
    abrir(opciones) {
      const esperando = opciones && opciones.esperando;
      if (esperando && !this.querySelector('[data-zv-lista]')) {
        this.classList.add('is-esperando');
      }

      if (this.hasAttribute('hidden')) this.removeAttribute('hidden');

      // Un cuadro de espera antes de la clase: si se saca el hidden y se
      // agrega la clase en el mismo cuadro, el navegador no ve el cambio de
      // estado y el panel aparece de golpe en vez de deslizarse.
      requestAnimationFrame(() => {
        this.classList.add('is-abierto');
        const panel = this.querySelector('[data-zv-panel]');
        if (panel) panel.focus();
      });

      this.anterior = document.activeElement;
      this.frenarPagina();
    }

    cerrar() {
      this.classList.remove('is-abierto');
      this.soltarPagina();

      // Se espera a que termine el deslizamiento antes de esconderlo: con
      // hidden puesto de una, la animacion de salida no se ve.
      window.setTimeout(() => {
        if (!this.classList.contains('is-abierto')) {
          this.setAttribute('hidden', '');
          this.classList.remove('is-esperando', 'is-cargando');
        }
      }, 300);

      if (this.anterior && typeof this.anterior.focus === 'function') this.anterior.focus();
    }

    alternar() {
      if (this.classList.contains('is-abierto')) this.cerrar();
      else this.abrir();
    }

    cerrando(evento) {
      if (evento.key === 'Escape' && this.classList.contains('is-abierto')) this.cerrar();
    }

    // Sin esto, al scrollear dentro del carrito se termina moviendo la pagina
    // de atras, que es de lo que mas se queja la gente en celulares.
    frenarPagina() {
      // Al sacar la barra de scroll la pagina se ensancha y todo el contenido
      // salta a la derecha justo cuando entra el panel. Se compensa con el
      // mismo ancho que se perdio. El panel no se mueve: vive en un contenedor
      // position:fixed, que se mide contra la ventana y no contra <html>.
      const barra = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      if (barra > 0) document.documentElement.style.paddingRight = barra + 'px';
    }

    soltarPagina() {
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
    }

    /* ---------------- Eventos de adentro ---------------- */

    alClick = (evento) => {
      if (evento.target.closest('[data-zv-cerrar]')) {
        this.cerrar();
        return;
      }

      const cant = evento.target.closest('[data-zv-cant]');
      if (cant) {
        this.cambiarLinea(cant);
        return;
      }

      const sumar = evento.target.closest('[data-zv-sumar]');
      if (sumar) {
        this.sumar(sumar.dataset.zvSumar, sumar);
      }
    };

    /* ---------------- Cambios en el carrito ---------------- */

    // Todo cambio pasa por aca: se anota como pendiente, se pone en la fila y
    // al volver se pinta solo si es el ultimo.
    //
    // `alFallar` es para los casos que se pueden reintentar — agregar algo sin
    // stock, por ejemplo. Si lo maneja, devuelve true y nadie recarga nada.
    encolar(hacer, alFallar) {
      this.pendientes += 1;
      this.classList.add('is-cargando');

      const seguir = () =>
        hacer()
          .then((respuesta) => {
            this.pendientes -= 1;
            return this.aplicar(respuesta);
          })
          .catch((error) => {
            this.pendientes -= 1;
            this.classList.remove('is-cargando', 'is-esperando');

            if (alFallar && alFallar(error) === true) return;

            // Sin nadie que lo maneje se recarga: es preferible perder la
            // posicion del scroll antes que dejar a la vista un carrito que
            // ya no es el real.
            window.location.reload();
          });

      this.cola = this.cola.then(seguir, seguir);
      return this.cola;
    }

    cambiarLinea(boton) {
      const linea = Number(boton.dataset.zvLinea);
      const nueva = Number(boton.dataset.zvNueva);

      this.adelantar(boton, nueva);

      return this.encolar(() =>
        pedirJSON('/cart/change.js', { line: linea, quantity: nueva }, SECCION)
      );
    }

    // Pinta el resultado antes de tenerlo. Lo importante no es solo el numero:
    // tambien hay que correr el data-zv-nueva de los dos botones, o un segundo
    // clic rapido vuelve a pedir la misma cantidad que el primero y el
    // contador se queda clavado.
    adelantar(boton, nueva) {
      const fila = boton.closest('[data-zv-item]');
      if (fila && nueva === 0) fila.classList.add('is-yendose');

      const caja = boton.closest('[data-zv-cant-caja]');
      if (!caja) return;

      const num = caja.querySelector('[data-zv-cant-num]');
      if (num) num.textContent = String(nueva);

      caja.querySelectorAll('[data-zv-paso]').forEach((otro) => {
        const paso = Number(otro.dataset.zvPaso);
        otro.dataset.zvNueva = String(Math.max(0, nueva + paso));
      });

      // El "−" se apaga en 1: de ahi para abajo esta "Quitar", que es
      // explicito. Antes bajaba a 0 y la linea desaparecia sin avisar.
      const menos = caja.querySelector('[data-zv-paso="-1"]');
      if (menos) menos.disabled = nueva <= 1;
    }

    sumar(id, boton) {
      const previo = boton.textContent;
      boton.disabled = true;
      boton.textContent = 'Agregando…';

      return this.encolar(
        () => pedirJSON('/cart/add.js', { items: [{ id: Number(id), quantity: 1 }] }, SECCION),
        () => {
          // Que un sugerido se haya quedado sin stock no es motivo para
          // recargar y perder el carrito que el cliente ya venia armando.
          boton.textContent = 'No se pudo';
          window.setTimeout(() => {
            boton.disabled = false;
            boton.textContent = previo;
          }, 1600);
          return true;
        }
      );
    }

    // Toma la respuesta de /cart/add.js o /cart/change.js y la pinta.
    aplicar(respuesta) {
      // Todavia vienen mas: que repinte el ultimo, asi el panel no cuenta en
      // voz alta cada paso intermedio.
      if (this.pendientes > 0) return Promise.resolve();

      const secciones = respuesta && respuesta.sections;
      const html = secciones && secciones[SECCION];

      // Sin seccion en la respuesta (Referer suprimido, o un error raro) se va
      // por el camino largo en vez de dejar el panel mintiendo.
      if (!html) return this.refrescar();

      this.pintar(html);

      // change.js devuelve el carrito entero; add.js devuelve solo la linea
      // agregada. En el primer caso no hace falta ir a buscar nada.
      const completo = respuesta && respuesta.item_count !== undefined ? respuesta : null;
      return this.actualizarBurbuja(completo);
    }

    pintar(html) {
      const nuevo = new DOMParser().parseFromString(html, 'text/html');
      const fresco = nuevo.querySelector('[data-zv-carrito]');
      if (!fresco) return window.location.reload();

      // Preservar el scroll: sin esto, tocar "+" en el tercer articulo de una
      // lista larga devuelve el panel al principio y hay que volver a bajar.
      const panelViejo = this.querySelector('[data-zv-panel]');
      const alto = panelViejo ? panelViejo.scrollTop : 0;
      const abierto = this.classList.contains('is-abierto');

      this.innerHTML = fresco.innerHTML;

      const panel = this.querySelector('[data-zv-panel]');
      if (panel && alto) panel.scrollTop = alto;

      // El HTML nuevo viene con hidden y sin la clase: hay que devolverle
      // el estado que tenia, o el panel se cierra solo al cambiar algo.
      this.classList.toggle('is-abierto', abierto);
      if (abierto) this.removeAttribute('hidden');

      this.classList.remove('is-cargando', 'is-esperando');
    }

    // Camino largo, solo para cuando no hay seccion en la respuesta o cuando
    // el cambio lo hizo otro (la ficha con varios colores, por ejemplo).
    //
    // Sin el header Accept: application/json. Parece de mas y es todo lo
    // contrario — en /products/* y /collections/* ese header hace que Shopify
    // conteste el JSON del RECURSO (el producto, la coleccion) e ignore el
    // ?sections=. La respuesta llega con 200 y es JSON valido, asi que no
    // entra por el catch: simplemente no trae la clave y el panel se quedaba
    // con el contenido viejo. Por eso habia que recargar para ver lo agregado.
    // En / no hay recurso que devolver, y ahi funcionaba: de ahi que pareciera
    // que a veces andaba. Horizon pide sus secciones sin ningun Accept
    // (assets/section-renderer.js:138).
    refrescar() {
      return fetch(window.location.pathname + '?sections=' + SECCION)
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((datos) => {
          // Si la seccion no vino, el panel a la vista ya no es el carrito
          // real. Antes esto era un return mudo, y por eso el bug de arriba
          // no daba ni un error en la consola: el carrito mentia en silencio.
          // Recargar es feo pero honesto.
          const html = datos[SECCION];
          if (!html) return window.location.reload();

          this.pintar(html);
          return this.actualizarBurbuja(null);
        })
        .catch(() => window.location.reload());
    }

    // El contador del encabezado es de Horizon y no se entera de estos
    // cambios. Hay que avisarle — pero con SU evento.
    //
    // Aca se emitia un CustomEvent llamado 'cart:update' con el carrito en
    // detail. Ese evento no lo escucha nadie: assets/cart-icon.js escucha
    // 'shopify:cart:lines-update' (el estandar de Shopify) y de el lee UNA
    // sola cosa, event.promise, que tiene que resolver a { cart }. Sin ese
    // nombre y sin esa promesa el numerito no se movia hasta recargar.
    //
    // El modulo se pide con import() dinamico a proposito. Con un import
    // estatico habria que volver este archivo un modulo, y si el CDN de
    // Shopify falla no cargaria NADA: ni abrir el carrito. Asi, lo peor que
    // pasa es que no se actualice el numerito. El modulo igual ya esta en la
    // pagina — cart-icon.js lo importa — asi que no cuesta una descarga mas.
    //
    // `conocido` es el carrito que ya vino en la respuesta del cambio. Cuando
    // esta, esto no cuesta ni un viaje de red.
    actualizarBurbuja(conocido) {
      const traer = conocido
        ? Promise.resolve(conocido)
        : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

      return Promise.all([traer, import('@shopify/events')])
        .then(([carrito, { CartLinesUpdateEvent }]) => {
          const plata = (centavos) => ({
            amount: (centavos / 100).toFixed(2),
            currencyCode: carrito.currency,
          });

          const lineas = carrito.items.map((item) => ({
            id: String(item.key),
            quantity: item.quantity,
            cost: { totalAmount: plata(item.final_line_price) },
          }));

          // Carrito vacio: por aca no se puede. CartLinesUpdateEvent valida el
          // payload contra un esquema que exige al menos una linea, y con
          // lines:[] tira "Too small: expected array to have >=1 items" y no
          // despacha nada — el numerito se quedaba en el ultimo valor despues
          // de vaciar el carrito. Se le habla directo al componente, que es lo
          // que el evento iba a terminar llamando igual.
          if (lineas.length === 0) {
            const icono = document.querySelector('cart-icon');
            if (icono && typeof icono.renderCartBubble === 'function') {
              icono.renderCartBubble(0, false);
            }
            return;
          }

          const pendiente = CartLinesUpdateEvent.createPromise();

          // action 'update' y no 'add': con 'add', el carrito lateral de
          // Horizon (que sigue en el tema, sin usar) se abriria solo y
          // tendriamos dos paneles abiertos a la vez.
          document.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'update',
              context: 'cart',
              lines: lineas.map(({ id, quantity }) => ({ id, quantity })),
              promise: pendiente.promise,
            })
          );

          pendiente.resolve({
            cart: {
              id: carrito.token,
              totalQuantity: carrito.item_count,
              cost: { totalAmount: plata(carrito.total_price) },
              lines: lineas,
              discountCodes: [],
            },
          });
        })
        .catch(() => {});
    }
  }

  if (!customElements.get('zenvox-carrito')) {
    customElements.define('zenvox-carrito', ZenvoxCarrito);
  }

  const carrito = () => document.querySelector('[data-zv-carrito]');

  /* ---------------- Enganches de afuera ---------------- */

  // Abrir desde el boton del encabezado, sin dejar que navegue a /cart.
  document.addEventListener('click', (evento) => {
    const disparador = evento.target.closest(DISPARADOR);
    if (!disparador) return;

    const cajon = carrito();
    if (!cajon) return;

    evento.preventDefault();
    evento.stopPropagation();
    cajon.abrir();
  });

  // Cualquier formulario de "agregar al carrito" de la tienda: la grilla, la
  // ficha, los sugeridos. Se manda por fetch para no perder la pagina.
  //
  // El panel se abre ANTES del fetch. Esa sola linea es la diferencia entre
  // "hice clic y no pasa nada" y "se abrio el carrito": el pedido tarda mas o
  // menos lo mismo, pero ahora pasa con el panel ya a la vista.
  document.addEventListener('submit', (evento) => {
    const form = evento.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/cart/add')) return;

    const cajon = carrito();
    if (!cajon) return;

    evento.preventDefault();

    const soltar = ocupar(form);
    cajon.abrir({ esperando: true });

    cajon
      .encolar(
        () => pedirForm('/cart/add.js', new FormData(form), SECCION),
        () => {
          // Se cae al envio normal del formulario: Shopify muestra su propio
          // error (sin stock, variante inexistente) en vez de una recarga muda.
          cajon.cerrar();
          form.submit();
          return true;
        }
      )
      .then(soltar, soltar);
  });

  // La ficha arma su propio pedido cuando hay varios colores. Avisa por acá,
  // y si ya tiene la respuesta del carrito la manda en el detail para que esto
  // no tenga que volver a preguntar.
  document.addEventListener('zenvox:carrito:cambio', (evento) => {
    const cajon = carrito();
    if (!cajon) return;

    const respuesta = evento.detail && evento.detail.respuesta;
    cajon.abrir({ esperando: true });

    if (respuesta) cajon.aplicar(respuesta);
    else cajon.refrescar();
  });

  // Abrir el panel sin cambiar nada, para que la ficha lo muestre apenas se
  // hace clic y no cuando vuelve el pedido.
  document.addEventListener('zenvox:carrito:abrir', () => {
    const cajon = carrito();
    if (cajon) cajon.abrir({ esperando: true });
  });

  /* ---------------- La página del carrito ---------------- */

  // Los mismos botones de cantidad, pero en /cart. Antes esto recargaba la
  // pagina entera en cada clic. Ahora repinta la seccion en el lugar, con la
  // misma jugada que el panel: el numero se adelanta y el HTML lo sigue
  // manda Liquid.
  const pagina = () => document.querySelector('[data-zv-pagina]');
  let colaPagina = Promise.resolve();

  document.addEventListener('click', (evento) => {
    const boton = evento.target.closest('[data-zv-cant]');
    if (!boton || boton.closest('[data-zv-carrito]')) return;

    const caja = pagina();
    if (!caja) return;

    evento.preventDefault();

    const linea = Number(boton.dataset.zvLinea);
    const nueva = Number(boton.dataset.zvNueva);
    const idSeccion = caja.dataset.zvPagina;

    // Lo mismo que hace el panel: pintar el numero antes de que vuelva nada.
    const grupo = boton.closest('[data-zv-cant-caja]');
    if (grupo) {
      const num = grupo.querySelector('[data-zv-cant-num]');
      if (num) num.textContent = String(nueva);
      grupo.querySelectorAll('[data-zv-paso]').forEach((otro) => {
        otro.dataset.zvNueva = String(Math.max(0, nueva + Number(otro.dataset.zvPaso)));
      });
      const menos = grupo.querySelector('[data-zv-paso="-1"]');
      if (menos) menos.disabled = nueva <= 1;
    }

    const fila = boton.closest('[data-zv-item]');
    if (fila && nueva === 0) fila.classList.add('is-yendose');

    caja.classList.add('is-cargando');

    const seguir = () =>
      pedirJSON('/cart/change.js', { line: linea, quantity: nueva }, idSeccion)
        .then((respuesta) => {
          const html = respuesta.sections && respuesta.sections[idSeccion];
          if (!html) return window.location.reload();

          const fresco = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-zv-pagina]');
          if (!fresco) return window.location.reload();

          const actual = pagina();
          if (!actual) return window.location.reload();

          actual.innerHTML = fresco.innerHTML;
          actual.classList.remove('is-cargando');

          const cajon = carrito();
          if (cajon) cajon.actualizarBurbuja(respuesta);
        })
        .catch(() => window.location.reload());

    colaPagina = colaPagina.then(seguir, seguir);
  });
})();
